import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  Gauge,
  Linkedin,
  MessagesSquare,
  Timer,
  Twitter,
} from "lucide-react";
import MarketingPage from "../../components/marketing/MarketingPage";
import {
  breadcrumbSchema,
  faqSchema,
  ORGANIZATION_SCHEMA,
  SITE_URL,
} from "../../components/SEO";
import { Breadcrumbs } from "./LeadGuidePage";
import {
  CHANNEL_COMPARISON,
  GUIDE_UPDATED,
  HUB_FAQS,
  LEAD_GUIDES,
} from "../../data/leadGuides";

// lucide-react has no Reddit brand glyph, so the guide cards use its generic
// threaded-conversation icon for Reddit.
const PLATFORM_ICON = {
  twitter: Twitter,
  linkedin: Linkedin,
  reddit: MessagesSquare,
};

// The three things that are true on every platform. Kept here rather than in
// the per-platform data because duplicating them across the guides would be
// exactly the thin, repeated content that gets a cluster discounted.
const FUNDAMENTALS = [
  {
    icon: Gauge,
    title: "Score before you spend attention",
    body: "Rate every lead 0–50 on intent (explicit request beats active complaint beats trigger event beats topical question) and 0–50 on ICP fit (title, company size, whether they can actually buy). Work anything above 70 today; everything else goes to a nurture list. Without a score you spend your best hour on whichever post you happened to see first.",
  },
  {
    icon: Timer,
    title: "Speed is most of the advantage",
    body: "A public request for a recommendation is answered by someone within hours. On X the useful window is about an hour; on LinkedIn it is a day or two; on Reddit the thread stays answerable for days and then keeps arriving from Google for years. Anything that shortens the gap between a post appearing and you reading it — saved searches, alerts, monitored feeds — is worth more than a better message.",
  },
  {
    icon: MessagesSquare,
    title: "Be useful in public before you ask in private",
    body: "The order that works on every network is engage, then connect, then ask. A specific public answer earns credibility with the author and with everyone reading; a cold pitch to a stranger burns the account it came from. On Reddit that rule is written down and enforced by moderators. Never open with the product — open with the problem they described.",
  },
];

export default function LeadGenHub() {
  const path = "/lead-generation";
  const url = `${SITE_URL}${path}`;

  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "How to find leads on X (Twitter), LinkedIn and Reddit",
    description:
      "Playbooks for finding high-intent B2B leads on X (Twitter), LinkedIn and Reddit — search operators, buying signals, outreach sequences and the daily routine behind them.",
    url,
    inLanguage: "en-US",
    dateModified: GUIDE_UPDATED,
    publisher: ORGANIZATION_SCHEMA,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: LEAD_GUIDES.map((g, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: g.seo.title,
        url: `${SITE_URL}${g.path}`,
      })),
    },
  };

  const breadcrumbs = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Lead generation", path },
  ]);

  return (
    <MarketingPage
      seo={{
        title: "How to Find Leads on X (Twitter), LinkedIn & Reddit",
        description:
          "Free playbooks for finding B2B leads on X (Twitter), LinkedIn and Reddit: the search strings that surface buying intent, the signals worth acting on, and which channel to start with.",
        path,
        keywords:
          "how to find leads on social media, social selling playbook, twitter vs linkedin vs reddit lead generation, X lead generation, linkedin lead generation, reddit lead generation, b2b social selling, buying intent signals, social lead generation guide",
        schema: [collection, faqSchema(HUB_FAQS), breadcrumbs],
      }}
    >
      <header className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(1200px 400px at 80% -20%, rgba(45,91,255,0.10), transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-12 pb-14 lg:pt-16">
          <Breadcrumbs
            trail={[{ name: "Home", to: "/" }, { name: "Lead generation" }]}
          />
          <p className="mt-6 text-[#2d5bff] font-medium text-sm tracking-wide uppercase">
            Playbooks
          </p>
          <h1 className="font-display text-[#0a1736] text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-3 max-w-4xl">
            How to find leads on X (Twitter), LinkedIn and Reddit{" "}
            <span className="italic text-[#2d5bff]">without cold outreach.</span>
          </h1>
          <p className="mt-6 text-lg text-[#5a6478] max-w-3xl leading-relaxed">
            Somebody described your product's exact problem in public this
            morning. These are the playbooks for finding those people — the
            search strings that surface buying intent, the eight signals worth
            acting on, and the outreach sequences that get a reply instead of a
            block. No automation tools, no scraped lists, nothing that risks
            your account.
          </p>
        </div>
      </header>

      <section
        className="bg-white border-t border-[#e3dccb]"
        aria-labelledby="guides-heading"
      >
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2
            id="guides-heading"
            className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold"
          >
            Start with your channel
          </h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {LEAD_GUIDES.map((g) => {
              const Icon = PLATFORM_ICON[g.key];
              return (
                <Link
                  key={g.key}
                  to={g.path}
                  className="group rounded-2xl border border-[#e3dccb] bg-[#faf6ee]/50 p-7 hover:border-[#2d5bff] hover:shadow-[0_14px_50px_-30px_rgba(45,91,255,0.5)] transition-all"
                >
                  <Icon className="w-7 h-7 text-[#2d5bff]" aria-hidden="true" />
                  <h3 className="font-display text-2xl text-[#0a1736] font-semibold mt-4 leading-snug">
                    How to find leads on {g.platform}
                  </h3>
                  <p className="mt-3 text-[#5a6478] leading-relaxed">
                    {g.seo.description}
                  </p>
                  <ul className="mt-5 space-y-1.5 text-sm text-[#5a6478]">
                    {g.methods.slice(0, 4).map((m) => (
                      <li key={m.id} className="flex gap-2">
                        <ChevronRight
                          className="w-4 h-4 text-[#2d5bff] shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        {m.title}
                      </li>
                    ))}
                    <li className="text-[#2d5bff] font-medium pl-6">
                      + {g.methods.length - 4} more methods
                    </li>
                  </ul>
                  <span className="mt-5 inline-flex items-center gap-2 text-[#0a1736] font-medium group-hover:text-[#2d5bff] transition-colors">
                    Read the playbook <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="bg-[#faf6ee] border-t border-[#e3dccb]"
        aria-labelledby="compare-heading"
      >
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2
            id="compare-heading"
            className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold"
          >
            X (Twitter) vs LinkedIn vs Reddit for lead generation
          </h2>
          <p className="mt-4 text-[#5a6478] max-w-3xl leading-relaxed">
            They are not substitutes. X is faster and more public, so intent
            shows up in plain language and you can answer within the hour.
            LinkedIn is more precise, so you can filter by title, headcount and
            tenure and reach functions that never post on X. Reddit is the most
            honest of the three — anonymity gets you real budgets and real
            churn reasons, and the threads keep ranking in Google for years. If
            you only have time for one, pick the one your buyers already talk
            on.
          </p>
          <div className="mt-8 overflow-x-auto rounded-xl border border-[#e3dccb] bg-white">
            <table className="w-full text-left text-sm min-w-[640px]">
              <caption className="sr-only">
                Comparison of X (Twitter), LinkedIn and Reddit as B2B lead generation channels
              </caption>
              <thead className="bg-[#0a1736] text-white">
                <tr>
                  {CHANNEL_COMPARISON.headers.map((h, i) => (
                    <th key={i} scope="col" className="px-5 py-3 font-medium">
                      {h || "Dimension"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CHANNEL_COMPARISON.rows.map((row) => (
                  <tr key={row[0]} className="border-t border-[#e3dccb] align-top">
                    <th scope="row" className="px-5 py-3 text-[#0a1736] font-semibold text-left">
                      {row[0]}
                    </th>
                    {row.slice(1).map((cell, i) => (
                      <td key={i} className="px-5 py-3 text-[#5a6478]">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section
        className="bg-white border-t border-[#e3dccb]"
        aria-labelledby="fundamentals-heading"
      >
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2
            id="fundamentals-heading"
            className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold"
          >
            Three things that are true on all three platforms
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {FUNDAMENTALS.map((f) => (
              <div
                key={f.title}
                className="rounded-xl border border-[#e3dccb] bg-[#faf6ee]/40 p-6"
              >
                <f.icon className="w-6 h-6 text-[#2d5bff]" aria-hidden="true" />
                <h3 className="font-display text-xl text-[#0a1736] font-semibold mt-3 leading-snug">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm text-[#5a6478] leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="bg-[#faf6ee] border-t border-[#e3dccb]"
        aria-labelledby="hub-faq-heading"
      >
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2
            id="hub-faq-heading"
            className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold"
          >
            Social lead generation: FAQ
          </h2>
          <div className="mt-8 space-y-3">
            {HUB_FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-[#e3dccb] bg-white p-5 open:border-[#2d5bff]/40"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <h3 className="text-base font-semibold text-[#0a1736]">{f.q}</h3>
                  <ChevronRight
                    className="w-5 h-5 text-[#2d5bff] shrink-0 transition-transform group-open:rotate-90"
                    aria-hidden="true"
                  />
                </summary>
                <p className="mt-3 text-[#5a6478] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a1736] text-white" aria-labelledby="hub-cta-heading">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2
            id="hub-cta-heading"
            className="font-display text-3xl md:text-4xl font-semibold leading-tight"
          >
            Or let Collavue run all three playbooks for you.
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto leading-relaxed">
            Continuous monitoring across X, LinkedIn, and Reddit, intent and ICP-fit
            scoring on every match, and reply drafts in your brand voice — with
            a human approving everything that goes out.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/signup"
              className="px-6 py-3 rounded-md bg-[#bef264] text-[#0a1736] font-semibold hover:bg-[#a3e635] transition-colors"
            >
              Start free
            </Link>
            <Link
              to="/pricing"
              className="px-6 py-3 rounded-md border border-white/20 text-white font-medium hover:border-white/60 transition-colors"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </MarketingPage>
  );
}
